<script setup lang="ts">
/**
 * Cloche de notifications globale (bandeau header).
 *
 * Affiche un compteur de messages non lus (alimenté par le store inbox, déjà
 * pollé toutes les 60 s) et, au clic, un panneau déroulant des dernières
 * notifications (messages Inbox : accomplissements débloqués, fins de
 * construction/recherche, rapports…). Un clic sur une notif la marque lue et
 * ouvre la boîte de réception ; « Voir tout » mène à /inbox.
 *
 * Distinct de l'icône Messages (/inbox) qui ouvre la vue complète : ici c'est
 * un aperçu rapide sans changer de page.
 */
import { computed, ref } from 'vue';
import { Bell } from '@element-plus/icons-vue';
import { useInboxStore } from '@/stores/inbox.store';
import { useSessionStore } from '@/stores/session.store';
import { useOverlaysStore } from '@/stores/overlays.store';
import { fetchInbox, markAsRead } from '@/api/inbox';
import type { InboxMessageModel, InboxMessageType } from '@/types/inbox';

const inbox = useInboxStore();
const session = useSessionStore();
const overlays = useOverlaysStore();

const visible = ref(false);
const loading = ref(false);
const items = ref<InboxMessageModel[]>([]);

const badge = computed(() => {
  const n = inbox.unreadCount;
  if (n <= 0) return null;
  return n > 99 ? '99+' : String(n);
});

async function load(): Promise<void> {
  if (session.activePlayerUniverseId == null) return;
  loading.value = true;
  try {
    const res = await fetchInbox({ ownerPlayerUniverseId: session.activePlayerUniverseId });
    // Les plus récents d'abord (le backend trie déjà, mais on sécurise).
    items.value = [...res.items]
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
      .slice(0, 6);
  } catch {
    items.value = [];
  } finally {
    loading.value = false;
  }
}

function onVisibleChange(v: boolean): void {
  visible.value = v;
  if (v) void load();
}

async function openMessage(m: InboxMessageModel): Promise<void> {
  if (!m.readAt) {
    try { await markAsRead(m.id); void inbox.refresh(); } catch { /* silencieux */ }
  }
  visible.value = false;
  overlays.openInbox();
}

function seeAll(): void {
  visible.value = false;
  overlays.openInbox();
}

const TYPE_ICON: Record<InboxMessageType, string> = {
  BattleReport: '⚔️', PrivateMessage: '✉️', System: '🔔', News: '📰',
  Trade: '💱', Espionage: '🕵️', Quest: '🎯',
  AllianceInvitation: '🤝', TreatyProposal: '📜', AllianceJoinRequest: '🤝',
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diff / 1000));
  if (s < 60) return 'à l’instant';
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}
</script>

<template>
  <el-popover
    :visible="visible"
    placement="bottom-end"
    :width="320"
    trigger="click"
    popper-class="hnb-popper"
    @update:visible="onVisibleChange"
  >
    <template #reference>
      <span class="hgn-icon" :class="{ 'is-active': visible }" aria-label="Notifications"
            :title="badge ? `Notifications (${badge})` : 'Notifications'">
        <el-icon :size="20"><Bell /></el-icon>
        <span v-if="badge" class="hgn-badge" aria-hidden="true">{{ badge }}</span>
      </span>
    </template>

    <div class="hnb">
      <header class="hnb-head">
        <strong>Notifications</strong>
        <el-button link size="small" @click="seeAll">Voir tout</el-button>
      </header>

      <div v-if="loading" class="hnb-empty">Chargement…</div>
      <div v-else-if="items.length === 0" class="hnb-empty">Aucune notification.</div>
      <ul v-else class="hnb-list">
        <li
          v-for="m in items"
          :key="m.id"
          class="hnb-item"
          :class="{ unread: !m.readAt }"
          @click="openMessage(m)"
        >
          <span class="hnb-ico">{{ TYPE_ICON[m.messageType] ?? '•' }}</span>
          <div class="hnb-body">
            <div class="hnb-subject">{{ m.subject }}</div>
            <div class="hnb-time">{{ relativeTime(m.created) }}</div>
          </div>
          <span v-if="!m.readAt" class="hnb-dot" aria-hidden="true" />
        </li>
      </ul>
    </div>
  </el-popover>
</template>

<style lang="scss" scoped>
// Réutilise les tokens du bandeau (mêmes .hgn-icon / .hgn-badge que HeaderGlobalNav).
.hgn-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: $radius-md;
  color: $text-secondary;
  cursor: pointer;
  transition: background $duration-fast $easing-default, color $duration-fast $easing-default;

  &:hover { background: $bg-tertiary; color: $color-neon-cyan; }
  &.is-active { background: rgba($accent-primary, 0.18); color: $color-neon-cyan; }
}
.hgn-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: $color-danger;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  font-family: $font-mono;
  box-shadow: 0 0 0 2px $bg-primary;
  pointer-events: none;
}

.hnb { display: flex; flex-direction: column; }
.hnb-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--el-border-color);
}
.hnb-empty {
  padding: 12px 4px;
  color: var(--el-text-color-secondary);
  font-size: 0.85em;
  text-align: center;
}
.hnb-list { list-style: none; margin: 0; padding: 0; max-height: 360px; overflow-y: auto; }
.hnb-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background $duration-fast $easing-default;

  &:hover { background: $bg-tertiary; }
  &.unread .hnb-subject { font-weight: 700; }
}
.hnb-ico { font-size: 1.05em; line-height: 1.3; }
.hnb-body { flex: 1; min-width: 0; }
.hnb-subject {
  font-size: 0.86em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hnb-time { font-size: 0.74em; color: var(--el-text-color-secondary); }
.hnb-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-top: 5px;
  border-radius: 50%;
  background: $color-danger;
}
</style>
